import { useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import { authClient } from "../lib/authClient";
import { Eye, EyeOff, ArrowLeft, ArrowRight, Lock, Mail, CheckCircle2 } from "lucide-react";
import { L, t } from "../lib/translations";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const USE_BETTER_AUTH = process.env.REACT_APP_USE_BETTER_AUTH === "true";

function GoogleLogo() {
  return (
    <svg aria-hidden="true" className="h-5 w-5 flex-shrink-0" viewBox="0 0 24 24">
      <path d="M23.52 12.27c0-.79-.07-1.55-.2-2.27H12v4.31h6.48a5.54 5.54 0 0 1-2.4 3.64v3.02h3.88c2.27-2.09 3.56-5.17 3.56-8.7Z" fill="#4285F4" />
      <path d="M12 24c3.24 0 5.95-1.07 7.93-2.91l-3.88-3.02c-1.07.72-2.44 1.15-4.05 1.15-3.11 0-5.74-2.1-6.68-4.92H1.3v3.11A12 12 0 0 0 12 24Z" fill="#34A853" />
      <path d="M5.32 14.3A7.21 7.21 0 0 1 4.95 12c0-.8.14-1.57.37-2.3V6.59H1.3A12 12 0 0 0 0 12c0 1.94.46 3.77 1.3 5.41l4.02-3.11Z" fill="#FBBC05" />
      <path d="M12 4.77c1.76 0 3.35.61 4.6 1.82l3.45-3.45C17.94 1.15 15.23 0 12 0A12 12 0 0 0 1.3 6.59L5.32 9.7C6.26 6.87 8.89 4.77 12 4.77Z" fill="#EA4335" />
    </svg>
  );
}

export default function Login() {
  const { user, setUser, checkAuth, blockedInfo } = useAuth();
  const { lang } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({ email: "", name: "", password: "" });
  const [submitting, setSubmitting] = useState(false);
  const [mode, setMode] = useState(() => searchParams.get("reset_token") ? "reset" : "sign-in");
  const [legalAccepted, setLegalAccepted] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSent, setForgotSent] = useState(false);
  const [resetForm, setResetForm] = useState({ password: "", confirm: "" });
  const [showResetPass, setShowResetPass] = useState(false);
  const [verifyEmailSent, setVerifyEmailSent] = useState("");

  const next = searchParams.get("next") || "/dashboard";
  const isRtl = lang === "he";

  const applyPendingRef = async () => {
    const code = localStorage.getItem("pending_ref");
    if (!code) return;
    try {
      const r = await fetch(`${API}/referral/apply`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ code }),
      });
      if (r.ok) toast.success(t("Login.referral_code_applied", lang));
    } catch {}
    localStorage.removeItem("pending_ref");
  };

  const getPendingCouponRedirect = () => {
    const coupon = localStorage.getItem("pending_coupon");
    if (!coupon) return null;
    localStorage.removeItem("pending_coupon");
    return `/coupon/${coupon}?activate=1`;
  };

  if (user) return <Navigate to={next} replace />;
  if (blockedInfo?.blocked) return <Navigate to="/blocked" replace />;

  const submit = async (event) => {
    event.preventDefault();
    const email = form.email.trim().toLowerCase();
    const name = form.name.trim();
    const password = form.password;
    if (!email || !password || (mode === "sign-up" && !name)) {
      toast.error(t("Login.fill_in_all_required_fields", lang)); return;
    }
    if (mode === "sign-up" && !legalAccepted) {
      toast.error(t("Login.please_accept_the_terms_to_continue", lang)); return;
    }
    setSubmitting(true);
    try {
      if (USE_BETTER_AUTH && mode === "sign-up") {
        const result = await authClient.signUp.email({ email, name, password, preferredLang: lang });
        if (result?.error) throw new Error(result.error.message || "Sign up failed");
        const appUser = await checkAuth();
        if (appUser?.blocked) { navigate("/blocked", { replace: true }); return; }
        if (!appUser) {
          await authClient.sendVerificationEmail({ email, callbackURL: "/dashboard" }).catch(() => {});
          setVerifyEmailSent(email);
          return;
        }
        setUser(appUser);
        if (!appUser.referred_by) await applyPendingRef();
        localStorage.setItem("pending_onboarding", "1");
        const dest = getPendingCouponRedirect();
        navigate(dest || next || "/dashboard", { replace: true });
        toast.success(t("Login.account_created", lang));
        return;
      }
      // Sign-in: try Better Auth first, fall back to legacy for existing users
      if (USE_BETTER_AUTH && mode === "sign-in") {
        const baResult = await authClient.signIn.email({ email, password });
        if (!baResult?.error) {
          const appUser = await checkAuth();
          if (appUser?.blocked) { navigate("/blocked", { replace: true }); return; }
          if (appUser) {
            setUser(appUser);
            if (!appUser.referred_by) await applyPendingRef();
            navigate(getPendingCouponRedirect() || next || "/dashboard", { replace: true });
            toast.success(t("Login.signed_in_successfully", lang));
            return;
          }
        }
        // Better Auth failed — fall through to legacy login
      }
      const endpoint = mode === "sign-up" ? `${API}/auth/register` : `${API}/auth/login`;
      const payload = mode === "sign-up" ? { email, name, password } : { email, password };
      const res = await fetch(endpoint, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Login failed");
      setUser(data);
      if (!data.referred_by) await applyPendingRef();
      if (mode === "sign-up") localStorage.setItem("pending_onboarding", "1");
      const dest2 = getPendingCouponRedirect();
      navigate(dest2 || next || "/dashboard", { replace: true });
      toast.success(mode === "sign-up" ? t("Login.account_created", lang) : t("Login.signed_in_successfully", lang));
    } catch (error) {
      toast.error(error.message || t("Login.login_failed", lang));
    } finally {
      setSubmitting(false);
    }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    const email = forgotEmail.trim().toLowerCase();
    if (!email) { toast.error(L(lang, "Enter your email", "Введите email", "הכנס אימייל")); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/auth/forgot-password`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Failed");
      setForgotSent(true);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    if (resetForm.password.length < 8) {
      toast.error(L(lang, "Password must be at least 8 characters", "Минимум 8 символов", "סיסמה חייבת להיות לפחות 8 תווים")); return;
    }
    if (resetForm.password !== resetForm.confirm) {
      toast.error(L(lang, "Passwords don't match", "Пароли не совпадают", "הסיסמאות אינן תואמות")); return;
    }
    const token = searchParams.get("reset_token");
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/auth/reset-password`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, new_password: resetForm.password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Failed");
      toast.success(L(lang, "Password updated! Sign in now.", "Пароль обновлён! Войдите.", "הסיסמה עודכנה! כנס עכשיו."));
      navigate("/login", { replace: true });
      setMode("sign-in");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const signInWithGoogle = async () => {
    if (!legalAccepted) {
      toast.error(t("Login.please_confirm_the_terms_to_continue", lang)); return;
    }
    try {
      setSubmitting(true);
      await fetch(`${API}/session/logout`, { method: "POST", credentials: "include" }).catch(() => {});
      const callbackURL = new URL(next || "/dashboard", window.location.origin).toString();
      const errorCallbackURL = new URL(`/login?next=${encodeURIComponent(next || "/dashboard")}`, window.location.origin).toString();
      const result = await authClient.signIn.social({ provider: "google", callbackURL, errorCallbackURL, disableRedirect: true });
      if (result?.error) throw new Error(result.error.message || "Google sign in failed");
      const redirectURL = result?.data?.url || result?.url;
      if (redirectURL) {
        window.location.assign(redirectURL);
      }
    } catch (error) {
      toast.error(error.message || t("Login.google_sign_in_failed", lang));
      setSubmitting(false);
    }
  };

  const inputCls = "w-full h-11 px-3.5 rounded-xl text-sm outline-none transition-all";
  const inputStyle = { background: "var(--surface-2)", border: "1px solid var(--border-color)", color: "var(--text-primary)" };

  if (verifyEmailSent) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 py-16" dir={isRtl ? "rtl" : "ltr"}>
        <div className="w-full max-w-md text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-3xl mb-6"
            style={{ background: "linear-gradient(135deg,rgba(225,48,108,0.15),rgba(131,58,180,0.15))", border: "1px solid rgba(225,48,108,0.25)" }}>
            <span style={{ fontSize: 40 }}>📧</span>
          </div>
          <h1 className="text-2xl font-black mb-3 tracking-tight" style={{ color: "var(--text-primary)" }}>
            {L(lang, "Check your inbox", "Проверьте почту", "בדקו את תיבת הדואר")}
          </h1>
          <p className="text-sm mb-2 leading-relaxed" style={{ color: "var(--text-secondary)" }}>
            {L(lang,
              "We sent a confirmation link to",
              "Мы отправили ссылку для подтверждения на",
              "שלחנו קישור אישור ל"
            )}
          </p>
          <p className="text-sm font-bold mb-6" style={{ color: "var(--text-primary)" }}>
            {verifyEmailSent}
          </p>
          <div className="rounded-2xl p-5 mb-6 text-start space-y-3"
            style={{ background: "var(--surface-1)", border: "1px solid var(--border-color)" }}>
            {[
              L(lang, "Open your email app", "Откройте приложение почты", "פתחו את אפליקציית הדואר"),
              L(lang, "Click the confirmation link", "Нажмите на ссылку подтверждения", "לחצו על קישור האישור"),
              L(lang, "Start using iFollowers 🚀", "Начните пользоваться iFollowers 🚀", "התחילו להשתמש ב-iFollowers 🚀"),
            ].map((step, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-black flex-shrink-0 text-white"
                  style={{ background: "linear-gradient(135deg,#E1306C,#833AB4)" }}>{i + 1}</div>
                <span className="text-sm" style={{ color: "var(--text-secondary)" }}>{step}</span>
              </div>
            ))}
          </div>
          <p className="text-xs mb-5" style={{ color: "var(--text-secondary)" }}>
            {L(lang, "Didn't get it? Check spam or", "Не получили? Проверьте спам или", "לא קיבלתם? בדקו ספאם או")}
            {" "}
            <button
              onClick={async () => {
                try {
                  await authClient.sendVerificationEmail({ email: verifyEmailSent, callbackURL: "/dashboard" });
                  toast.success(L(lang, "Sent again!", "Отправили снова!", "נשלח שוב!"));
                } catch {
                  toast.error(L(lang, "Failed to resend", "Ошибка отправки", "שגיאה בשליחה"));
                }
              }}
              className="font-semibold underline underline-offset-2 hover:opacity-70 transition-opacity"
              style={{ color: "var(--accent-primary)" }}>
              {L(lang, "resend", "отправить снова", "שלחו שוב")}
            </button>
          </p>
          <button
            onClick={() => { setVerifyEmailSent(""); setMode("sign-in"); }}
            className="text-sm hover:opacity-70 transition-opacity"
            style={{ color: "var(--text-secondary)" }}>
            ← {L(lang, "Back to sign in", "Вернуться ко входу", "חזרה להתחברות")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-14 pb-20 flex items-center justify-center px-4" dir={isRtl ? "rtl" : "ltr"}>
      <div className="w-full max-w-[400px]">

        {/* Logo */}
        <div className="flex flex-col items-center mb-8">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-white font-black text-xl mb-3.5"
            style={{ background: "linear-gradient(135deg,#8B5CF6,#4F46E5)" }}>
            iF
          </div>
          <h1 className="text-2xl font-black font-outfit tracking-tight" style={{ color: "var(--text-primary)" }}>
            iFollowers
          </h1>
          <p className="text-xs mt-1.5 font-medium" style={{ color: "var(--text-secondary)" }}>
            {L(lang, "Social media growth for Israel", "Продвижение в социальных сетях", "קידום ברשתות חברתיות")}
          </p>
        </div>

        {/* Card */}
        <div className="rounded-2xl overflow-hidden border" style={{ borderColor: "var(--border-color)", background: "var(--surface-1)" }}>

          {/* Forgot / Reset modes — simplified header */}
          {(mode === "forgot" || mode === "reset") ? (
            <div className="p-5">
              <div className="flex items-center gap-3 mb-5">
                {mode === "forgot" && (
                  <button type="button" onClick={() => { setMode("sign-in"); setForgotSent(false); setForgotEmail(""); }}
                    className="w-8 h-8 rounded-xl flex items-center justify-center transition-all hover:opacity-70"
                    style={{ background: "var(--surface-2)", border: "1px solid var(--border-color)", color: "var(--text-secondary)" }}>
                    {isRtl ? <ArrowRight className="w-4 h-4" /> : <ArrowLeft className="w-4 h-4" />}
                  </button>
                )}
                <div>
                  <div className="text-sm font-bold" style={{ color: "var(--text-primary)" }}>
                    {mode === "forgot"
                      ? L(lang, "Recover account", "Восстановление аккаунта", "שחזור חשבון")
                      : L(lang, "Set new password", "Новый пароль", "הגדרת סיסמה חדשה")}
                  </div>
                  <div className="text-[11px] mt-0.5" style={{ color: "var(--text-secondary)" }}>
                    {mode === "forgot"
                      ? L(lang, "We'll send a reset link to your email", "Отправим ссылку на ваш email", "נשלח קישור לאיפוס לאימייל שלך")
                      : L(lang, "Enter your new password below", "Введите новый пароль ниже", "הזן את הסיסמה החדשה שלך למטה")}
                  </div>
                </div>
              </div>

              {/* Forgot form */}
              {mode === "forgot" && !forgotSent && (
                <form onSubmit={submitForgot} className="space-y-3.5">
                  <div className="space-y-1.5">
                    <label className="block text-[10px] font-bold uppercase tracking-wider" style={{ color: "var(--text-secondary)" }}>
                      {t("Login.email", lang)}
                    </label>
                    <input type="email" autoComplete="email" placeholder="name@example.com"
                      value={forgotEmail} onChange={e => setForgotEmail(e.target.value)}
                      className={inputCls} style={inputStyle}
                      onFocus={e => { e.target.style.borderColor = "rgba(139,92,246,0.5)"; e.target.style.boxShadow = "0 0 0 3px rgba(139,92,246,0.1)"; }}
                      onBlur={e => { e.target.style.borderColor = "var(--border-color)"; e.target.style.boxShadow = "none"; }} />
                  </div>
                  <button type="submit" disabled={submitting}
                    className="w-full h-12 rounded-xl text-sm font-bold text-white transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ background: "linear-gradient(135deg,#8B5CF6,#4F46E5)" }}>
                    {submitting ? (
                      <span className="flex items-center justify-center gap-2">
                        <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                        {t("Login.please_wait", lang)}
                      </span>
                    ) : L(lang, "Send reset link", "Отправить ссылку", "שלח קישור לאיפוס")}
                  </button>
                </form>
              )}

              {/* Forgot success */}
              {mode === "forgot" && forgotSent && (
                <div className="flex flex-col items-center gap-3 py-4 text-center">
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ background: "rgba(34,197,94,0.12)" }}>
                    <CheckCircle2 className="w-7 h-7" style={{ color: "#22c55e" }} />
                  </div>
                  <div className="text-sm font-bold" style={{ color: "var(--text-primary)" }}>
                    {L(lang, "Check your inbox", "Проверьте почту", "בדוק את תיבת הדואר")}
                  </div>
                  <div className="text-[11px] leading-relaxed" style={{ color: "var(--text-secondary)" }}>
                    {L(lang,
                      <>We sent a reset link to <span className="font-semibold" style={{ color: "var(--text-primary)" }}>{forgotEmail}</span>. Check your spam folder if you don't see it.</>,
                      <>Ссылка отправлена на <span className="font-semibold" style={{ color: "var(--text-primary)" }}>{forgotEmail}</span>. Проверьте папку «Спам».</>,
                      <>שלחנו קישור לאיפוס ל-<span className="font-semibold" style={{ color: "var(--text-primary)" }}>{forgotEmail}</span>. בדוק בתיקיית הספאם.</>
                    )}
                  </div>
                  <button type="button" onClick={() => { setMode("sign-in"); setForgotSent(false); setForgotEmail(""); }}
                    className="mt-1 text-xs font-semibold underline transition-opacity hover:opacity-70"
                    style={{ color: "#8B5CF6" }}>
                    {L(lang, "Back to sign in", "Вернуться к входу", "חזרה להתחברות")}
                  </button>
                </div>
              )}

              {/* Reset form */}
              {mode === "reset" && (
                <form onSubmit={submitReset} className="space-y-3.5">
                  <div className="space-y-1.5">
                    <label className="block text-[10px] font-bold uppercase tracking-wider" style={{ color: "var(--text-secondary)" }}>
                      {L(lang, "New password", "Новый пароль", "סיסמה חדשה")}
                    </label>
                    <div className="relative">
                      <input type={showResetPass ? "text" : "password"} autoComplete="new-password"
                        placeholder={t("Login.at_least_8_characters", lang)}
                        value={resetForm.password} onChange={e => setResetForm(c => ({ ...c, password: e.target.value }))}
                        className="w-full h-11 rounded-xl text-sm outline-none transition-all"
                        style={{ ...inputStyle, padding: isRtl ? "0 14px 0 44px" : "0 44px 0 14px" }}
                        onFocus={e => { e.target.style.borderColor = "rgba(139,92,246,0.5)"; e.target.style.boxShadow = "0 0 0 3px rgba(139,92,246,0.1)"; e.target.style.padding = isRtl ? "0 14px 0 44px" : "0 44px 0 14px"; }}
                        onBlur={e => { e.target.style.borderColor = "var(--border-color)"; e.target.style.boxShadow = "none"; e.target.style.padding = isRtl ? "0 14px 0 44px" : "0 44px 0 14px"; }} />
                      <button type="button" onClick={() => setShowResetPass(v => !v)} tabIndex={-1}
                        className="absolute top-1/2 -translate-y-1/2 p-1 rounded-md transition-colors hover:bg-white/5"
                        style={{ [isRtl ? "left" : "right"]: "10px", color: "var(--text-secondary)" }}>
                        {showResetPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <label className="block text-[10px] font-bold uppercase tracking-wider" style={{ color: "var(--text-secondary)" }}>
                      {L(lang, "Confirm password", "Подтвердите пароль", "אישור סיסמה")}
                    </label>
                    <input type={showResetPass ? "text" : "password"} autoComplete="new-password"
                      placeholder={t("Login.at_least_8_characters", lang)}
                      value={resetForm.confirm} onChange={e => setResetForm(c => ({ ...c, confirm: e.target.value }))}
                      className={inputCls} style={inputStyle}
                      onFocus={e => { e.target.style.borderColor = "rgba(139,92,246,0.5)"; e.target.style.boxShadow = "0 0 0 3px rgba(139,92,246,0.1)"; }}
                      onBlur={e => { e.target.style.borderColor = "var(--border-color)"; e.target.style.boxShadow = "none"; }} />
                  </div>
                  <button type="submit" disabled={submitting}
                    className="w-full h-12 rounded-xl text-sm font-bold text-white transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ background: "linear-gradient(135deg,#8B5CF6,#4F46E5)" }}>
                    {submitting ? (
                      <span className="flex items-center justify-center gap-2">
                        <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                        {t("Login.please_wait", lang)}
                      </span>
                    ) : L(lang, "Update password", "Обновить пароль", "עדכן סיסמה")}
                  </button>
                  <p className="text-center text-[11px]" style={{ color: "var(--text-secondary)" }}>
                    <button type="button" onClick={() => { navigate("/login", { replace: true }); setMode("sign-in"); }}
                      className="underline hover:opacity-70 transition-opacity" style={{ color: "#8B5CF6" }}>
                      {L(lang, "Back to sign in", "Вернуться к входу", "חזרה להתחברות")}
                    </button>
                  </p>
                </form>
              )}
            </div>
          ) : (
            <>
              {/* Tabs */}
              <div className="grid grid-cols-2 gap-1 p-1.5" style={{ background: "var(--surface-2)", borderBottom: "1px solid var(--border-color)" }}>
                {[["sign-in", t("NotFound.sign_in", lang)], ["sign-up", t("Login.register", lang)]].map(([val, label]) => (
                  <button key={val} type="button" onClick={() => setMode(val)}
                    className="h-9 rounded-xl text-sm font-bold transition-all duration-150"
                    style={{ background: mode === val ? "#8B5CF6" : "transparent", color: mode === val ? "#fff" : "var(--text-secondary)" }}>
                    {label}
                  </button>
                ))}
              </div>

              <div className="p-5 space-y-4">

                {/* Terms checkbox — shown first so user knows they must accept */}
                <label className="flex cursor-pointer items-start gap-3 select-none rounded-xl p-3"
                  style={{ background: "var(--surface-2)", border: `1.5px solid ${legalAccepted ? "rgba(34,197,94,0.3)" : "var(--border-color)"}` }}>
                  <input type="checkbox" className="sr-only" checked={legalAccepted} onChange={e => setLegalAccepted(e.target.checked)} />
                  <div className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-md transition-all duration-200"
                    style={{ background: legalAccepted ? "#22c55e" : "var(--surface-1)", border: `1.5px solid ${legalAccepted ? "transparent" : "var(--border-color)"}` }}>
                    {legalAccepted && (
                      <svg className="w-3 h-3" viewBox="0 0 12 12" fill="none">
                        <path d="M2 6l3 3 5-5" stroke="white" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </div>
                  <span className="text-[11px] leading-relaxed" style={{ color: "var(--text-secondary)" }}>
                    {L(lang,
                      <>I agree to the <Link to="/terms" target="_blank" className="underline" style={{ color: "#8B5CF6" }}>Terms</Link>, <Link to="/warranty" target="_blank" className="underline" style={{ color: "#4F46E5" }}>Warranty</Link> &amp; <Link to="/privacy" target="_blank" className="underline" style={{ color: "#0EA5E9" }}>Privacy Policy</Link></>,
                      <>Согласен с <Link to="/terms" target="_blank" className="underline" style={{ color: "#8B5CF6" }}>Условиями</Link>, <Link to="/warranty" target="_blank" className="underline" style={{ color: "#4F46E5" }}>Гарантией</Link> и <Link to="/privacy" target="_blank" className="underline" style={{ color: "#0EA5E9" }}>Конфиденциальностью</Link></>,
                      <>אני מסכים/ה ל<Link to="/terms" target="_blank" className="underline" style={{ color: "#8B5CF6" }}>תקנון</Link>, <Link to="/warranty" target="_blank" className="underline" style={{ color: "#4F46E5" }}>אחריות</Link> ו<Link to="/privacy" target="_blank" className="underline" style={{ color: "#0EA5E9" }}>מדיניות פרטיות</Link></>
                    )}
                  </span>
                </label>

                {/* Google button */}
                {USE_BETTER_AUTH && (
                  <button type="button" disabled={submitting} onClick={signInWithGoogle}
                    className="w-full h-12 rounded-xl flex items-center justify-center gap-3 font-semibold text-sm transition-all duration-150 disabled:opacity-50"
                    style={{ background: "var(--surface-2)", border: "1px solid var(--border-color)", color: "var(--text-primary)" }}
                    data-testid="login-google-btn">
                    {submitting
                      ? <span className="w-4 h-4 border-2 border-zinc-400 border-t-transparent rounded-full animate-spin" />
                      : <GoogleLogo />}
                    {t("Login.continue_with_google", lang)}
                  </button>
                )}

                {/* Divider */}
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-px" style={{ background: "var(--border-color)" }} />
                  <span className="text-[11px] font-medium" style={{ color: "var(--text-secondary)" }}>
                    {L(lang, "or with email", "или по email", "או עם אימייל")}
                  </span>
                  <div className="flex-1 h-px" style={{ background: "var(--border-color)" }} />
                </div>

                {/* Email form */}
                <form onSubmit={submit} className="space-y-3.5">
                  {mode === "sign-up" && (
                    <div className="space-y-1.5">
                      <label className="block text-[10px] font-bold uppercase tracking-wider" style={{ color: "var(--text-secondary)" }}>
                        {t("Login.your_name", lang)}
                      </label>
                      <input type="text" autoComplete="name"
                        placeholder={t("Login.full_name", lang)}
                        value={form.name} onChange={e => setForm(c => ({ ...c, name: e.target.value }))}
                        className={inputCls} style={inputStyle}
                        onFocus={e => { e.target.style.borderColor = "rgba(139,92,246,0.5)"; e.target.style.boxShadow = "0 0 0 3px rgba(139,92,246,0.1)"; }}
                        onBlur={e => { e.target.style.borderColor = "var(--border-color)"; e.target.style.boxShadow = "none"; }} />
                    </div>
                  )}

                  <div className="space-y-1.5">
                    <label className="block text-[10px] font-bold uppercase tracking-wider" style={{ color: "var(--text-secondary)" }}>
                      {t("Login.email", lang)}
                    </label>
                    <input type="email" autoComplete="email" placeholder="name@example.com"
                      value={form.email} onChange={e => setForm(c => ({ ...c, email: e.target.value }))}
                      className={inputCls} style={inputStyle}
                      onFocus={e => { e.target.style.borderColor = "rgba(139,92,246,0.5)"; e.target.style.boxShadow = "0 0 0 3px rgba(139,92,246,0.1)"; }}
                      onBlur={e => { e.target.style.borderColor = "var(--border-color)"; e.target.style.boxShadow = "none"; }} />
                  </div>

                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <label className="block text-[10px] font-bold uppercase tracking-wider" style={{ color: "var(--text-secondary)" }}>
                        {t("Login.password", lang)}
                      </label>
                      {mode === "sign-in" && (
                        <button type="button" onClick={() => setMode("forgot")}
                          className="text-[10px] font-semibold transition-opacity hover:opacity-70"
                          style={{ color: "#8B5CF6" }}>
                          {L(lang, "Forgot password?", "Забыли пароль?", "שכחת סיסמה?")}
                        </button>
                      )}
                    </div>
                    <div className="relative">
                      <input type={showPass ? "text" : "password"}
                        autoComplete={mode === "sign-up" ? "new-password" : "current-password"}
                        placeholder={t("Login.at_least_8_characters", lang)}
                        value={form.password} onChange={e => setForm(c => ({ ...c, password: e.target.value }))}
                        className="w-full h-11 rounded-xl text-sm outline-none transition-all"
                        style={{ ...inputStyle, padding: isRtl ? "0 14px 0 44px" : "0 44px 0 14px" }}
                        onFocus={e => { e.target.style.borderColor = "rgba(139,92,246,0.5)"; e.target.style.boxShadow = "0 0 0 3px rgba(139,92,246,0.1)"; e.target.style.padding = isRtl ? "0 14px 0 44px" : "0 44px 0 14px"; }}
                        onBlur={e => { e.target.style.borderColor = "var(--border-color)"; e.target.style.boxShadow = "none"; e.target.style.padding = isRtl ? "0 14px 0 44px" : "0 44px 0 14px"; }} />
                      <button type="button" onClick={() => setShowPass(v => !v)} tabIndex={-1}
                        className="absolute top-1/2 -translate-y-1/2 p-1 rounded-md transition-colors hover:bg-white/5"
                        style={{ [isRtl ? "left" : "right"]: "10px", color: "var(--text-secondary)" }}>
                        {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <button type="submit" disabled={submitting}
                    className="w-full h-12 rounded-xl text-sm font-bold text-white transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ background: "linear-gradient(135deg,#8B5CF6,#4F46E5)" }}>
                    {submitting ? (
                      <span className="flex items-center justify-center gap-2">
                        <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                        {t("Login.please_wait", lang)}
                      </span>
                    ) : mode === "sign-up" ? t("Login.create_account", lang) : t("NotFound.sign_in", lang)}
                  </button>
                </form>
              </div>
            </>
          )}
        </div>

        <p className="mt-5 text-center text-xs">
          <Link to="/" className="inline-flex items-center gap-1.5 transition-colors hover:opacity-80" style={{ color: "var(--text-secondary)" }}>
            {isRtl ? <ArrowRight className="w-3 h-3" /> : <ArrowLeft className="w-3 h-3" />}
            {t("Login.back_to_home", lang)}
          </Link>
        </p>

      </div>
    </div>
  );
}
